import { supabase } from "@/integrations/supabase/client";

interface KlineRow {
  pair: string;
  interval: string;
  open_time: string;
  close_time: string;
  open_price: number;
  high_price: number;
  low_price: number;
  close_price: number;
  volume: number;
  quote_volume: number;
  trades_count: number;
}

/**
 * Parse Binance CSV kline data into database rows
 * CSV format: open_time, open, high, low, close, volume, close_time, quote_volume, trades, taker_buy_vol, taker_buy_quote_vol, ignore
 * Timestamps are in microseconds
 */
export function parseBinanceCsv(csvData: string, pair: string, interval: string = "1m"): KlineRow[] {
  const lines = csvData.trim().split("\n");
  const klines: KlineRow[] = [];

  for (const line of lines) {
    if (!line.trim()) continue;
    
    const parts = line.split(",");
    if (parts.length < 11) continue;
    
    // Timestamps are in microseconds, need to convert to milliseconds
    const openTimeMicro = parseInt(parts[0]);
    const closeTimeMicro = parseInt(parts[6]);
    
    const openTimeMs = Math.floor(openTimeMicro / 1000);
    const closeTimeMs = Math.floor(closeTimeMicro / 1000);
    
    klines.push({
      pair,
      interval,
      open_time: new Date(openTimeMs).toISOString(),
      close_time: new Date(closeTimeMs).toISOString(),
      open_price: parseFloat(parts[1]),
      high_price: parseFloat(parts[2]),
      low_price: parseFloat(parts[3]),
      close_price: parseFloat(parts[4]),
      volume: parseFloat(parts[5]),
      quote_volume: parseFloat(parts[7]),
      trades_count: parseInt(parts[8]),
    });
  }

  return klines;
}

/**
 * Parse indicator-format CSV (TradingView export) into database rows
 * CSV format: time,open,high,low,close,Histogram,MACD,Signal,Volume,...
 * Timestamps are in seconds
 */
export function parseIndicatorCsv(csvData: string, pair: string, interval: string = "1m"): KlineRow[] {
  const lines = csvData.trim().split("\n");
  const klines: KlineRow[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    
    // Skip header row
    if (line.startsWith("time,")) continue;
    
    const parts = line.split(",");
    if (parts.length < 9) continue;
    
    // Timestamps are in seconds, need to convert to milliseconds
    const openTimeSec = parseInt(parts[0]);
    if (isNaN(openTimeSec)) continue;
    
    const openTimeMs = openTimeSec * 1000;
    // Close time is open + 60 seconds for 1m interval
    const closeTimeMs = openTimeMs + 60000 - 1;
    
    const volume = parseFloat(parts[8]) || 0;
    
    klines.push({
      pair,
      interval,
      open_time: new Date(openTimeMs).toISOString(),
      close_time: new Date(closeTimeMs).toISOString(),
      open_price: parseFloat(parts[1]),
      high_price: parseFloat(parts[2]),
      low_price: parseFloat(parts[3]),
      close_price: parseFloat(parts[4]),
      volume: volume,
      quote_volume: 0, // Not available in indicator format
      trades_count: 0, // Not available in indicator format
    });
  }

  return klines;
}

/**
 * Import klines from Binance CSV format to the database in batches
 */
export async function importKlinesToDb(
  csvData: string, 
  pair: string, 
  interval: string = "1m",
  onProgress?: (progress: { current: number; total: number }) => void
): Promise<{ success: boolean; inserted: number; errors: string[] }> {
  const klines = parseBinanceCsv(csvData, pair, interval);
  return importKlinesToDbInternal(klines, onProgress);
}

/**
 * Import klines from Indicator CSV format to the database in batches
 */
export async function importIndicatorCsvToDb(
  csvData: string, 
  pair: string, 
  interval: string = "1m",
  onProgress?: (progress: { current: number; total: number }) => void
): Promise<{ success: boolean; inserted: number; errors: string[] }> {
  const klines = parseIndicatorCsv(csvData, pair, interval);
  return importKlinesToDbInternal(klines, onProgress);
}

/**
 * Internal function to import parsed klines to the database
 */
async function importKlinesToDbInternal(
  klines: KlineRow[],
  onProgress?: (progress: { current: number; total: number }) => void
): Promise<{ success: boolean; inserted: number; errors: string[] }> {
  
  if (klines.length === 0) {
    return { success: false, inserted: 0, errors: ["No valid klines found in CSV"] };
  }

  // Sort by open_time to ensure proper ordering
  klines.sort((a, b) => new Date(a.open_time).getTime() - new Date(b.open_time).getTime());

  const batchSize = 500;
  let inserted = 0;
  const errors: string[] = [];

  for (let i = 0; i < klines.length; i += batchSize) {
    const batch = klines.slice(i, i + batchSize);
    
    const { error } = await supabase
      .from("historical_klines")
      .upsert(batch, { 
        onConflict: "pair,interval,open_time",
        ignoreDuplicates: true 
      });

    if (error) {
      errors.push(`Batch ${Math.floor(i / batchSize) + 1}: ${error.message}`);
    } else {
      inserted += batch.length;
    }

    if (onProgress) {
      onProgress({ current: Math.min(i + batchSize, klines.length), total: klines.length });
    }
  }

  return { 
    success: errors.length === 0, 
    inserted, 
    errors 
  };
}

/**
 * Get date range of imported klines
 */
export async function getKlinesDateRange(pair: string, interval: string = "1m") {
  const { data: first } = await supabase
    .from("historical_klines")
    .select("open_time")
    .eq("pair", pair)
    .eq("interval", interval)
    .order("open_time", { ascending: true })
    .limit(1)
    .single();

  const { data: last } = await supabase
    .from("historical_klines")
    .select("open_time")
    .eq("pair", pair)
    .eq("interval", interval)
    .order("open_time", { ascending: false })
    .limit(1)
    .single();

  const { count } = await supabase
    .from("historical_klines")
    .select("*", { count: "exact", head: true })
    .eq("pair", pair)
    .eq("interval", interval);

  return {
    from: first?.open_time,
    to: last?.open_time,
    count: count || 0
  };
}
